import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import moment from 'moment';
import { isFirefox } from 'react-device-detect';

import { Box, CircularProgress, Typography, TableCell, Tooltip } from '@mui/material';

// comps
import GlobalButton from '../../components/global/button';
import GlobalInput from '../../components/global/input';
import GlobalCheckbox from '../../components/global/checkbox';
import FilterContainer from '../../components/layout/filterContainer';
import RoomSearchCard from '../../components/grid/roomSearchCard';
import DataGrid from '../../components/grid/dataGrid';
import GlobalDropdown from '../../components/global/dropdown';

// icons
import { ReactComponent as RefreshIcon } from '../../assets/icons/refresh-icon.svg';
import { ReactComponent as CalendarIcon } from '../../assets/icons/calendar-icon.svg';
import { ReactComponent as HelpIcon } from '../../assets/icons/help-icon.svg';

// api
import RoomApi from '../../api/room.api';
import WeekdaysApi from '../../api/weekdays.api';
import BuildingApi from '../../api/building.api';

// constants
import { colors } from '../../theme';
import { MOMENT_TIME_FORMAT } from '../../constants/date_format';
import { useAppSelector } from '../../hooks/hooks';
import { FACILITIES_VIEW_TYPE } from '../../constants';

const RoomSearch = (props: any) => {
    const _roomApi = new RoomApi();
    const _weekDayApi = new WeekdaysApi();
    const _buildingApi = new BuildingApi();
    const appState = useAppSelector((state) => state.appState);

    const [isFilterDisabled, setIsFilterDisabled] = useState(true);

    const defaultFilterObject = {
        startDate: null,
        endDate: null,
        timeIn: '00:00',
        timeOut: '23:59',
        maxSlotsOccupied: 0,
        selectedDays: [],
    };
    const [state, setState] = useState({
        selectedRoom: null,
        roomData: [],
        generateGrid: false,
        filterObject: defaultFilterObject,
        isLoading: false,
        isAlertOpen: false,
        weekdays: [],
        buildings: [],
    });

    const [selected, setSelected] = useState({
        building: null,
    });

    const multipleBuildings = state.buildings?.length > 1;

    const loadData = async () => {
        const days = state.filterObject.selectedDays.map((item) => item.id);
        const { startDate, endDate, timeIn, timeOut, maxSlotsOccupied } = state.filterObject;

        //TODO: (PS) clean up date and time formatting
        const roomSearchResult = await _roomApi.RoomSearch(selected.building?.id, days, startDate, endDate, timeIn + ':00', timeOut + ':00', maxSlotsOccupied);

        setState({
            ...state,
            roomData: roomSearchResult,
            isLoading: false,
            generateGrid: true,
            selectedRoom: null,
        });
    };

    const handleGenerateGrid = async () => {
        const { startDate, endDate, timeIn, timeOut } = state.filterObject;

        if (moment(endDate).isBefore(startDate)) {
            toast.error('End Date must be AFTER Start Date.');
            return;
        }

        if (timeOut?.length >= 4 && moment(timeOut, MOMENT_TIME_FORMAT).isBefore(moment(timeIn, MOMENT_TIME_FORMAT))) {
            toast.error('End Time must be AFTER Start Time.');
            return;
        }

        if (timeIn?.length >= 4 && moment(timeIn, MOMENT_TIME_FORMAT).isAfter(moment(timeOut, MOMENT_TIME_FORMAT))) {
            toast.error('Start Time must be BEFORE End Time.');
            return;
        } else {
            setState({ ...state, generateGrid: true, isLoading: true });
            await loadData().catch((error) => toast.error("Error generating grid data"));
        }
    };

    const handleChange = (name: any, value: any, item: any) => {
        if (value === '') value = null;

        if (name === 'weekday') {
            let selectedDays = [...state.filterObject.selectedDays];
            const isDuplicate = selectedDays.some((d) => d.fullName === item.fullName);
            if (isDuplicate) {
                const index = selectedDays.indexOf(item);
                if (index > -1) {
                    selectedDays.splice(index, 1);
                }
            } else {
                selectedDays.push(item);
            }

            setState({
                ...state,
                filterObject: {
                    ...state.filterObject,
                    selectedDays,
                },
            });
        } else {
            setState({
                ...state,
                filterObject: {
                    ...state.filterObject,
                    [name]: value,
                },
            });
        }
    };

    const handleClearFilter = () => {
        setState({
            ...state,
            generateGrid: false,
            filterObject: defaultFilterObject,
        });
    };

    const handleSelectRoom = (room: any) => {
        setState({
            ...state,
            selectedRoom: room,
        });
    };

    const loadBuildingsData = async (viewType: string, campusId?: number) => {
        const weekdays = await _weekDayApi.GetAll();
        const buildings = await _buildingApi.GetAllBuildings(viewType === FACILITIES_VIEW_TYPE ? campusId : null);
        let selectedBuilding = null;

        //Auto select if there is only one building. The building dropdown will be hidden
        if (buildings.length === 1) {
            selectedBuilding = buildings[0];
        }
        setState({
            ...state,
            generateGrid: false,
            filterObject: defaultFilterObject,
            roomData: [],
            buildings,
            weekdays,
        });

        setSelected({
            ...selected,
            building: selectedBuilding,
        });
    };

    useEffect(() => {
        const { startDate, endDate, timeIn, timeOut, selectedDays } = state.filterObject;
        if (!startDate || !endDate || !timeIn || !timeOut || selectedDays.length === 0) {
            setIsFilterDisabled(true);
        } else {
            setIsFilterDisabled(false);
        }
    }, [state.filterObject]);

    useEffect(() => {
        loadBuildingsData(appState.viewType, appState.campus?.id).catch((error) => toast.error('Error loading data'));
    }, [appState.campus]);

    const handleBuildingChange = (name: any, value: any) => {
        setSelected({ ...selected, building: value });
    };

    const renderRoomGrid = (data: any) => {
        return (
            <>
                <TableCell
                    sx={{
                        color: colors.blue.main,
                        fontWeight: 500,
                        width: '4%',
                    }}
                >
                    {data.roomNumber}
                </TableCell>
                <TableCell sx={{ width: '15%' }}>{data.roomType}</TableCell>
                <TableCell sx={{ width: '40%' }}>{data.roomComments}</TableCell>
                <TableCell sx={{ width: '3%', textAlign: 'center' }}>{data.roomCapacity}</TableCell>
                <TableCell sx={{ width: '8%', textAlign: 'center' }}>
                    {data.slotsOccupied} / {data.totalSlots}
                </TableCell>
            </>
        );
    };

    return (
        <>
            <FilterContainer sx={{ justifyContent: 'space-between' }}>
                <FlexBox sx={{ flexDirection: 'column' }}>
                    <FlexBox>
                        {/* No need to show the building dropdown if there is only one building */}
                        {multipleBuildings && (
                            <GlobalDropdown
                                label="Building"
                                placeholder="Select..."
                                name="name"
                                field="id"
                                width="130px"
                                dataItemKey={'building'}
                                sx={{ mr: '5px' }}
                                value={selected.building}
                                data={state.buildings}
                                onChange={handleBuildingChange}
                                isSearch
                            />
                        )}
                        <GlobalInput
                            label="Start Date"
                            placeholder="Start Date"
                            type={'date'}
                            name="startDate"
                            value={state.filterObject.startDate}
                            onChange={handleChange}
                            sx={{ mr: '5px', width: '133px' }}
                        />
                        <GlobalInput
                            label="end date"
                            placeholder="End Date"
                            type={'date'}
                            name="endDate"
                            onChange={handleChange}
                            value={state.filterObject.endDate}
                            sx={{ mr: '5px', width: '133px' }}
                        />
                        <GlobalInput label="Start Time" isTime={true} onChange={handleChange} name="timeIn" value={state.filterObject.timeIn} sx={{ mr: '5px', width: '95px' }} />
                        <GlobalInput label="End Time" isTime={true} onChange={handleChange} name="timeOut" value={state.filterObject.timeOut} sx={{ mr: '5px', width: '95px' }} />
                        {isFirefox && (
                            <Tooltip
                                arrow
                                title={<span style={{ whiteSpace: 'pre-line' }}>{`You can either use your keyboard arrow keys \nor manually type in values to change date and time.`}</span>}
                            >
                                <Box sx={{ display: 'flex', alginItems: 'center', svg: { width: '24px', height: 'auto', path: { fill: colors.grey.medium } } }}>
                                    <HelpIcon />
                                </Box>
                            </Tooltip>
                        )}
                    </FlexBox>
                    <FlexBox sx={{ width: '100%', mt: '10px' }}>
                        <GlobalInput
                            label="# max slots occupied "
                            type={'number'}
                            parse={'int'}
                            name="maxSlotsOccupied"
                            value={state.filterObject.maxSlotsOccupied}
                            inputProps={{ min: '0' }}
                            onChange={handleChange}
                            sx={{ mr: '10px', maxWidth: '80px' }}
                        />
                        {state.weekdays.map((o: any, i: number) => {
                            return (
                                <GlobalCheckbox
                                    key={i}
                                    isChecked={state.filterObject.selectedDays.includes(o)}
                                    name="weekday"
                                    onChange={(name: any, value: any) => handleChange(name, value, o)}
                                    label={o.shortName}
                                />
                            );
                        })}
                    </FlexBox>
                </FlexBox>
                <FlexBox sx={{ flexDirection: 'column', width: '13%' }}>
                    <GlobalButton variant="round" text="clear all" fullWidth secondary={'true'} sx={{ mb: '10px' }} endIcon={<RefreshIcon />} onClick={handleClearFilter} />
                    <GlobalButton variant="round" text="search" disabled={isFilterDisabled} fullWidth onClick={handleGenerateGrid} />
                </FlexBox>
            </FilterContainer>
            <Box sx={{ display: 'flex', minHeight: '75vh' }}>
                {state.generateGrid ? (
                    state.isLoading ? (
                        <ContentBox
                            sx={{
                                backgroundColor: colors.white,
                                borderRadius: '5px',
                                height: 'calc(100vh - 190px)',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                flexDirection: 'column',
                            }}
                        >
                            <CircularProgress size={100} />
                        </ContentBox>
                    ) : (
                        <>
                            <Box sx={{ flex: 1 }}>
                                <DataGrid
                                    clickable
                                    customsize
                                    borderradius="5px 0px 0px 5px"
                                    topHeight={18}
                                    columns={roomGridColumns}
                                    data={state.roomData}
                                    activeCell={state.selectedRoom}
                                    dataItemKey={'roomId'}
                                    renderRows={renderRoomGrid}
                                    onRowClick={(item: any) => handleSelectRoom(item)}
                                    height={'70vh'}
                                />
                            </Box>
                            <ContentBox
                                sx={{
                                    background: colors.grey.light,
                                    borderradius: '0px 5px 5px 0px',
                                    display: 'flex',
                                    alignItems: state.selectedRoom ? 'flex-start' : 'center',
                                    justifyContent: state.selectedRoom ? 'flex-start' : 'center',
                                    flexDirection: state.selectedRoom ? 'column' : 'row',
                                }}
                            >
                                {!state.selectedRoom ? (
                                    <Box
                                        sx={{
                                            textAlign: 'center',
                                            svg: {
                                                path: {
                                                    fill: colors.grey.medium,
                                                },
                                            },
                                        }}
                                    >
                                        <CalendarIcon />
                                        <Typography variant="body1" color={colors.grey.medium}>
                                            No room selected yet
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box
                                        sx={{
                                            width: '100%',
                                        }}
                                    >
                                        <Header variant="h4">
                                            Room Schedule - {state.selectedRoom.roomNumber} {state.selectedRoom.roomType && '(' + state.selectedRoom.roomType + ')'}
                                        </Header>
                                        <RoomSearchCard selectedRoom={state.selectedRoom} showDate={state.filterObject.startDate && state.filterObject.endDate} />
                                    </Box>
                                )}
                            </ContentBox>
                        </>
                    )
                ) : (
                    <ContentBox
                        sx={{
                            backgroundColor: colors.white,
                            borderRadius: '5px',
                            height: 'calc(100vh - 190px)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flexDirection: 'column',
                        }}
                    >
                        <CalendarIcon />
                        <Typography sx={{ mt: '15px' }} color={colors.blue.main}>
                            Please enter in the fields above to display rooms and their availability.
                        </Typography>
                    </ContentBox>
                )}
            </Box>
        </>
    );
};

export default RoomSearch;

const FlexBox = styled(Box)`
    && {
        display: flex;
        align-items: center;
    }
`;

const ContentBox = styled(Box)`
    && {
        box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.05);
        padding: 0px 15px;
        margin-top: 10px;
        flex: 1;
        overflow-y: scroll;
    }
`;

const Header = styled(Typography)`
    && {
        border-bottom: 1px solid ${colors.grey.medium};
        padding: 15px 0px;
        margin-bottom: 10px;
        color: ${colors.grey.dark};
    }
`;

const roomGridColumns = [
    {
        label: 'Room',
        disabled: true,
    },
    {
        label: 'Type',
        disabled: true,
    },
    {
        label: 'Configuration',
        disabled: true,
    },
    {
        label: 'Capacity',
        sx: {
            textAlign: 'center',
        },
        disabled: true,
    },
    {
        label: 'Slots',
        sx: {
            textAlign: 'center',
        },
        disabled: true,
    },
];
